'use strict'

import Vue from 'vue'
import Api from '@/api/videointerview.js'
import axios from 'axios'
import { parseResponse } from '@/_helpers/utils.js'

const getNextIndex = (function () {
  let identifier = 0
  return () => {
    return identifier++
  }
})()

const setQuestionNames = function () {
  state.videointerview.questions.forEach((item, index) => { item.name = `Question ${index + 1}` })
}

const state = {
  loading: true,
  error: null,
  videointerview: null,
  deletedQuestions: [],
  index: 0,
  is_playing: false,
  autoPlay: false,
  lastSaveTimestamp: null,
  lastUpdateTimestamp: null
}

const actions = {
  fetch ({ commit, dispatch }, { vacancyId }) {
    if (!vacancyId) {
      throw new Error('Empty vacancy id')
    }

    commit('fetch')

    return Api.fetchOneByVacancyId(vacancyId)
      .then(data => commit('fetchSuccess', data))
      .catch(err => {
        dispatch('global/setError', err, {root: true})
        commit('fetchFailure', err)
      })
  },

  save ({ commit, dispatch, state }, model) {
    if (!model.id) {
      throw new Error('Videointerview empty id')
    }

    commit('save')

    return Api.updateOne(model)
      .then(() => {
        let questions = [...model.questions];
        questions = questions.map(item => {
          return Object.assign({}, item, {
            is_video_answer_enabled: state.videointerview.is_video_answer_enabled
          });
        });
        const questionsToDelete = state.deletedQuestions;
        const questionsToCreate = (questions || []).filter(question => !question.id);
        const questionsToUpdate = (questions || []).filter(question => question.id);

        return Promise.all([
          questionsToDelete && questionsToDelete.length > 0 ? Api.removeQuestions(model.id, questionsToDelete) : null,
          questionsToCreate && questionsToCreate.length > 0 ? Api.storeQuestions(model.id, questionsToCreate) : null,
          questionsToUpdate && questionsToUpdate.length > 0 ? Api.updateQuestions(model.id, questionsToUpdate) : null
        ]);
      })
      .then(() => {
        dispatch('fetch', {
          vacancyId: model.vacancy.id
        })
        commit('saveChanges')
      })
      .catch(err => {
        dispatch('global/setError', err, { root: true })
        commit('saveFailure', err)
        throw err
      })
  },

  save_video_question ({ commit, dispatch }, model) {
    let data = new FormData()
    data.append('video[source]', model.blob)
    axios.post(`/1.0/video/save-video`, data)
    .then(response => {
      const data = parseResponse(response).data[0];
      return data
    })
    .catch(err => {
      return Promise.reject(err);
    })
  },

  disable ({commit, dispatch, state}, model) {
    commit('save')

    const data = {
      id: model.id,
      is_active: false
    }

    return Api.updateOne(data)
      .then(data => commit('saveSuccess', data))
      .catch(err => {
        dispatch('global/setError', err, {root: true})
        commit('saveFailure', err)
      })
  },

  enable ({commit, dispatch, state}, model) {
    commit('save')

    const data = {
      id: model.id,
      is_active: true
    }

    return Api.updateOne(data)
      .then(data => commit('saveSuccess', data))
      .catch(err => {
        dispatch('global/setError', err, {root: true})
        commit('saveFailure', err)
      })
  },

  addQuestion ({commit}) {
    commit('addQuestion')
  },

  saveQuestion ({ commit, state, dispatch }, { model }) {
    const request = model.id ? Api.updateQuestions : Api.storeQuestions

    request(state.videointerview.id, [model])
      .then(() => dispatch('fetch', { vacancyId: state.videointerview.id }))
      .catch(err => {
        dispatch('global/setError', err, {root: true})
        commit('saveFailure', err)
      })
  },

  deleteQuestion ({ commit, dispatch, state }, { model }) {
    commit('deleteQuestion', { model })
    Api.removeQuestions(state.videointerview.id, [model])
      .catch(err => {
        dispatch('global/setError', err, {root: true})
        commit('saveFailure', err)
      })
  },

  updateQuestions ({ commit, dispatch, state }, { questions }) {
    commit('updateQuestions', questions)

    Api.updateQuestions(state.videointerview.id, questions)
      .then(() => dispatch('fetch', { vacancyId: state.videointerview.id }))
      .catch(err => {
        dispatch('global/setError', err, {root: true})
        commit('saveFailure', err)
      })
  }
}

const mutations = {
  fetch (state) {
    // state.videointerview = null
    state.loading = true
  },

  fetchSuccess(state, data) {
    // Strange: in browser Network tab questions has one order
    // but in Axios.get().then() - another order
    // So we doing sort here
    delete data.description; // Описание теперь находится в /api/v3/vacancy-stages/videointerview/${vacancyId} отсюда убираем, чтобы при сохрании v1 не переписывало v3

    state.videointerview = data;
    state.videointerview.questions = state.videointerview.questions
      .map(question => {
        return Object.assign({}, question, {
          _id: question.id || question.index,
          _youtube_error: false
        });
      })
      .sort((a, b) => a.priority - b.priority);
    state.deletedQuestions = [];
    state.loading = false;
  },

  fetchFailure (state, err) {
    state.error = err
    state.loading = false
  },

  update(state, model = {}) {
    state.videointerview = Object.assign({}, state.videointerview, model);
    state.lastUpdateTimestamp = new Date()
  },
  setActive (state, { isActive }) {
    state.videointerview.is_active = isActive
  },

  updateQuestion (state, {model, data}) {
    Object.assign(model, data)
    state.lastUpdateTimestamp = new Date()
  },

  updateQuestions (state, questions) {
    state.videointerview.questions = questions
    state.lastUpdateTimestamp = new Date()
  },

  addQuestion (state) {
    const maxPriority = Math.max(...state.videointerview.questions.map(q => q.priority))

    state.videointerview && state.videointerview.questions && state.videointerview.questions.push({
      name: '',
      body: '',
      answer_time: 60,
      preparation_time: 30,
      is_required: true,
      is_en: false, // this is used in specialprocess shlumberger
      retryable: false,
      priority: maxPriority + 100,
      video: null,
      video_title: null,
      video_url: null,
      index: getNextIndex(),
      _id: getNextIndex(),
      _youtube_error: false
    })

    setQuestionNames()
    state.lastUpdateTimestamp = new Date()
  },

  deleteQuestion (state, {model}) {
    let index

    if (model.id) {
      index = state.videointerview.questions.findIndex(item => item.id === model.id)
    } else {
      index = state.videointerview.questions.findIndex(item => item.index === model.index)
    }

    state.deletedQuestions.push(model)

    Vue.delete(state.videointerview.questions, index)

    setQuestionNames()
    state.lastUpdateTimestamp = new Date()
  },

  save (state) {
    state.loading = true
  },

  saveSuccess (state, data) {
    state.videointerview = data
    state.lastSaveTimestamp = new Date()
  },

  saveFailure (state, err) {
    state.loading = false
    state.error = err
    console.error(err)
  },

  setIndex (state, index) {
    state.index = index
  },

  set_playing (state, is_play) {
    state.is_playing = is_play
  },

  setAutoPlay (state, autoPlay) {
    state.autoPlay = autoPlay
  },

  resetChangesTimestamps (state) {
    state.lastSaveTimestamp = null
    state.lastUpdateTimestamp = null
  },

  saveChanges (state) {
    state.lastSaveTimestamp = new Date()
  },

  clear (state) {
    state.videointerview = null;
  }
}

const getters = {
  error (state, getters) {
    return state.error
  },

  loading (state, getters) {
    return state.loading
  },

  is_playing (state, getters) {
    return state.is_playing
  },

  index (state) {
    return state.index
  },

  one (state, getters) {
    return state.videointerview || {}
  },

  haveUnsavedChanges (state) {
    return state.lastUpdateTimestamp > state.lastSaveTimestamp
  },

  questions: state => {
    if (!state.videointerview) {
      return [];
    }
    return state.videointerview.questions
  },

  get_question: state => id => state.videointerview.questions.find(question => +question.id === id)
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
}
